"use client";

import { create } from "zustand";
import {
  normalizeSectionSortKeys,
  safeGenerateKeyBetween,
} from "./fractional-keys";
import { toast } from "sonner";
import { syncScrollFieldsToQueryCache } from "@/lib/client/scroll-query-cache-bridge";
import { resolveBlockType } from '../registry/blocks-registry-resolve';
import { getDefaultData, isImplementedType } from '../registry/sections-registry';
import { clampTabTitle, normalizeTabsBlockData } from '../models/tabs-model';
import {
  createRuleRow,
  createStepRow,
  ensureRuleRowsWithIds,
  ensureStepRowsWithIds,
} from '../models/semantic-row-model';
import {
  MAX_DAYS,
  createMilestone,
  normalizeJourneyTimelineData,
} from '../models/journey-timeline-model';
import { HERO_BLOCK_DEFAULTS } from '../Blocks/shared/defaults/block-seeds';
import {
  findLegacyInnerBlockLocation,
  getActiveTabContentSections,
  isStructuralSectionType,
} from "../layout/notebook-layout";
import { isTabBlockLimitReached } from "../layout/tab-block-limits";
import { notifyTabBlockAddBlocked } from "../layout/tab-block-limit-toasts";
import {
  createEmptyNotebook,
  createTab,
  notebookFromApi,
  expandBlankNotebookTabs,
  buildTheme,
  findTabIdForSection,
  getActiveTab,
  MAX_TABS,
} from '../models/notebook-model';
import {
  fetchNotebook,
  loadNotebook as persistLoad,
  saveNotebook as persistSave,
  updateNotebookMeta,
  deleteNotebook as apiDeleteNotebook,
  addSection as apiAddSection,
  saveSectionData,
  deleteSection as apiDeleteSection,
  reorderSections as apiReorderSections,
  saveToLocalStorage,
  addNotebookBlock,
  scheduleBlockSave,
  purgeWriteQueueForNotebook,
  NotebookApiError,
} from './persistence';
import { nbLog, nbWarn, snapshot as nbSnapshot } from './notebook-debug';
import {
  clearNotebookLocalCache,
  readNotebookLocalCache,
  writeNotebookLocalCache,
} from './notebook-local-cache';
import { lifecycleFieldsFromApi } from '../lifecycle/apply-lifecycle';
import { deactivateActiveEditor } from './active-editor-context';

function logContentReadyEpoch(source, notebookId, readyEpoch) {
  nbLog('store', 'contentReadyEpoch set (blocks not layout-measured yet)', {
    source,
    notebookId,
    contentReadyEpoch: readyEpoch,
    sessionEpoch: readyEpoch,
    perfNow: typeof performance !== 'undefined' ? performance.now() : 0,
  })
}

const hydrateInflight = new Map()
const lifecycleSyncInflight = new Map()
const sectionTimers = new Map();
let metaTimer = null;
const MAX_UNDO_PER_BLOCK = 20;
/** @type {Map<string, object[]>} */
const undoStacksByBlock = new Map();

function nowIso() {
  return new Date().toISOString();
}

function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return hash;
}

function sectionExistsInNotebook(notebook, sectionId) {
  if (!notebook?.tabs || !sectionId) return false;
  return notebook.tabs.some((tab) =>
    tab.sections?.some((section) => section.id === sectionId),
  );
}

function findSectionDataInNotebook(notebook, sectionId) {
  for (const tab of notebook?.tabs ?? []) {
    const match = tab.sections?.find((section) => section.id === sectionId);
    if (match) return match.data;
  }
  return undefined;
}

function cancelSectionSaveTimer(sectionId) {
  const pending = sectionTimers.get(sectionId);
  if (pending) {
    clearTimeout(pending);
    sectionTimers.delete(sectionId);
  }
}

function cancelAllSectionSaveTimers() {
  for (const id of sectionTimers.keys()) {
    cancelSectionSaveTimer(id);
  }
}

function isNotebookInteractionLocked(getState) {
  const s = getState?.() ?? {};
  return Boolean(s.publicMode || s.locked);
}

/** Flush in-flight section debounces (tab switch, blur, route leave). */
async function flushPendingSectionSaves(get, hooks) {
  const { notebookId, notebook } = get();
  if (!notebookId) return;
  const pendingIds = [...sectionTimers.keys()];
  if (!pendingIds.length) return;

  let hadError = false;
  for (const sectionId of pendingIds) {
    cancelSectionSaveTimer(sectionId);
    const data = findSectionDataInNotebook(notebook, sectionId);
    if (data === undefined) continue;
    try {
      await saveSectionData(notebookId, sectionId, data);
    } catch (err) {
      if (err instanceof NotebookApiError && err.status === 404) continue;
      hadError = true;
    }
  }
  if (hadError) hooks?.onError?.();
  else hooks?.onSaved?.();
}

function remapSectionSaveTimer(oldSectionId, newSectionId, notebookId, getState, hooks) {
  cancelSectionSaveTimer(oldSectionId);
  if (!newSectionId || newSectionId === oldSectionId || !notebookId || !getState) {
    return;
  }
  const { notebook } = getState();
  if (!sectionExistsInNotebook(notebook, newSectionId)) return;
  const data = findSectionDataInNotebook(notebook, newSectionId);
  if (data === undefined) return;
  scheduleSectionSave(notebookId, newSectionId, data, hooks, getState);
}

function scheduleSectionSave(notebookId, sectionId, data, hooks, getState) {
  if (!notebookId || !sectionId) return;
  if (isNotebookInteractionLocked(getState)) return;
  if (getState && !sectionExistsInNotebook(getState().notebook, sectionId)) {
    cancelSectionSaveTimer(sectionId);
    return;
  }
  cancelSectionSaveTimer(sectionId);
  sectionTimers.set(
    sectionId,
    setTimeout(async () => {
      try {
        if (getState && !sectionExistsInNotebook(getState().notebook, sectionId)) {
          return;
        }
        hooks?.onSaving?.();
        await saveSectionData(notebookId, sectionId, data);
        hooks?.onSaved?.();
      } catch (err) {
        if (err instanceof NotebookApiError && err.status === 404) {
          cancelSectionSaveTimer(sectionId);
          return;
        }
        hooks?.onError?.(err);
      } finally {
        sectionTimers.delete(sectionId);
      }
    }, 400),
  );
}

function scheduleMetaSave(notebookId, patch, hooks, getState) {
  if (!notebookId) return;
  if (isNotebookInteractionLocked(getState)) return;
  clearTimeout(metaTimer);
  metaTimer = setTimeout(async () => {
    try {
      hooks?.onSaving?.();
      await updateNotebookMeta(notebookId, patch);
      if (patch.title !== undefined) {
        syncScrollFieldsToQueryCache(notebookId, { title: patch.title });
      }
      hooks?.onSaved?.();
    } catch (err) {
      hooks?.onError?.(err);
    }
  }, 600);
}

function handlePlanReadOnlySaveError(set, err) {
  if (err instanceof NotebookApiError && err.code === "PLAN_READ_ONLY") {
    set({
      planAccessPrompt: "note",
      planReadOnly: true,
      locked: true,
      isDirty: true,
      isSaving: false,
      saveStatus: "error",
    });
    return true;
  }
  return false;
}

function createSectionSaveHooks(set, get) {
  return {
    onSaving: () => set({ isSaving: true, saveStatus: "saving" }),
    onSaved: () => get().markSaved(),
    onError: (err) => {
      if (handlePlanReadOnlySaveError(set, err)) return;
      set({ isDirty: true, isSaving: false, saveStatus: "error" });
    },
  };
}

function patchTabsSection(set, get, sectionId, runtimeData) {
  const tid =
    findTabIdForSection(get().notebook, sectionId) ?? get().activeTabId;
  patchNotebook(set, get, (nb) => ({
    ...nb,
    tabs: nb.tabs.map((t) =>
      t.id === tid
        ? {
            ...t,
            sections: t.sections.map((s) =>
              s.id === sectionId
                ? { ...s, data: normalizeTabsBlockData(runtimeData) }
                : s,
            ),
          }
        : t,
    ),
  }));
}

function patchNotebook(set, get, updater) {
  const prev = get().notebook;
  const next = typeof updater === "function" ? updater(prev) : updater;
  set({
    notebook: next,
    title: next.title ?? "",
    isDirty: true,
    saveStatus: "pending",
  });
}

function syncSaved(set, lastSaved) {
  set({
    isDirty: false,
    isSaving: false,
    lastSaved: lastSaved ?? new Date(),
    lastSavedAt: lastSaved?.toISOString?.() ?? new Date().toISOString(),
    saveStatus: "saved",
  });
}

/** Drop duplicate section rows (bad migrations created twin block_tabs, etc.). */
function dedupeNotebookSections(notebook) {
  if (!notebook?.tabs) return notebook;
  return {
    ...notebook,
    tabs: notebook.tabs.map((tab) => {
      const seen = new Set();
      let hasTabsBlock = false;
      const sections = normalizeSectionSortKeys(
        tab.sections.filter((section) => {
          if (!section?.id || seen.has(section.id)) return false;
          seen.add(section.id);
          const type = resolveBlockType(section.type);
          if (type === "block_tabs") {
            if (hasTabsBlock) return false;
            hasTabsBlock = true;
          }
          return true;
        }),
      );
      return { ...tab, sections };
    }),
  };
}

export const notebookStateCreator = (set, get) => ({
  /** Bumps on each route notebook session — keys editors to force remount. */
  sessionEpoch: 0,
  /** Matches sessionEpoch when hydrate finished — gates shell content vs stale data. */
  contentReadyEpoch: -1,
  /** From nextNotebookRouteVisit() — new value on every SPA entry to /notebook/[id]. */
  routeVisit: -1,
  hydrated: false,
  notebookId: null,
  notebook: createEmptyNotebook(""),
  activeTabId: "",
  isDirty: false,
  isSaving: false,
  lastSaved: null,
  locked: false,
  lifecycleView: 'active',
  isDeleted: false,
  restoreRequiresFolder: false,
  parentFolderId: null,
  parentFolderName: null,
  workspaceId: null,
  restoreBusy: false,
  /** Readonly publication surface — disables persistence and editor chrome. */
  publicMode: false,
  isPinned: false,
  isArchived: false,
  launcherOpen: false,
  launcherTarget: { kind: "notebook" },
  /** Brief id for launchpad insert spawn animation on BlockSection */
  lastSpawnSectionId: null,
  freemiumPrompt: null,
  planAccessPrompt: null,
  planReadOnly: false,
  /** Section id → open Rich Text insert-block menu (edge +). */
  richTextInsertSectionId: null,
  /** Section id → open media file picker (edge +). */
  mediaAppendSectionId: null,
  /** Section id → open reference URL input (edge +). */
  referenceAppendSectionId: null,
  // Legacy flat fields used by TopBar, AutoSaveIndicator, NotebookShell
  title: "",
  saveStatus: "idle",
  lastSavedAt: null,
  currentPage: 0,
  tocOpen: true,

  markDirty: () => set({ isDirty: true, saveStatus: "pending" }),
  markSaved: () => syncSaved(set, new Date()),

  clearSpawnSection: () => set({ lastSpawnSectionId: null }),

  flushPendingSaves: () => {
    const hooks = createSectionSaveHooks(set, get);
    return flushPendingSectionSaves(get, hooks);
  },

  pushUndoSnapshot: (blockId, data) => {
    if (!blockId || !data) return;
    const stack = undoStacksByBlock.get(blockId) ?? [];
    stack.push(structuredClone(data));
    if (stack.length > MAX_UNDO_PER_BLOCK) stack.shift();
    undoStacksByBlock.set(blockId, stack);
  },

  popUndoSnapshot: (blockId) => {
    if (!blockId) return null;
    const stack = undoStacksByBlock.get(blockId);
    if (!stack?.length) return null;
    return stack.pop() ?? null;
  },

  setLauncherOpen: (v) =>
    set({
      launcherOpen: v,
      ...(v ? {} : { launcherTarget: { kind: "notebook" } }),
    }),

  openLauncher: (target = { kind: "notebook" }) => {
    if (get().publicMode) return;
    if (get().locked) {
      if (get().planReadOnly) set({ planAccessPrompt: "note" });
      return;
    }
    set({ launcherOpen: true, launcherTarget: target });
  },

  openRichTextInsertMenu: (sectionId) =>
    set({ richTextInsertSectionId: sectionId }),

  clearRichTextInsertMenu: () => set({ richTextInsertSectionId: null }),

  openMediaAppend: (sectionId) => set({ mediaAppendSectionId: sectionId }),

  clearMediaAppend: () => set({ mediaAppendSectionId: null }),

  openReferenceAppend: (sectionId) =>
    set({ referenceAppendSectionId: sectionId }),

  clearReferenceAppend: () => set({ referenceAppendSectionId: null }),

  openLauncherForTab: (tabsSectionId, tabId) => {
    if (get().publicMode) return;
    if (get().locked) {
      if (get().planReadOnly) set({ planAccessPrompt: "note" });
      return;
    }
    set({
      launcherOpen: true,
      launcherTarget: { kind: "tab", tabsSectionId, tabId },
    });
  },

  hasTabsBlock: () => {
    const { notebook } = get();
    return notebook.tabs.some((t) =>
      t.sections.some((s) => resolveBlockType(s.type) === "block_tabs"),
    );
  },
  setTocOpen: (v) => set({ tocOpen: v }),
  setCurrentPage: (n) => set({ currentPage: n }),
  dismissFreemiumPrompt: () => set({ freemiumPrompt: null }),
  dismissPlanAccessPrompt: () => set({ planAccessPrompt: null }),
  promptPlanReadOnly: (kind = "note") => set({ planAccessPrompt: kind }),

  /**
   * Full reset when leaving /notebook/* (Activity-hidden or unmounted route).
   */
  endNotebookSession: () => {
    const leavingNotebookId = get().notebookId;
    for (const timer of sectionTimers.values()) {
      clearTimeout(timer);
    }
    sectionTimers.clear();
    if (metaTimer) {
      clearTimeout(metaTimer);
      metaTimer = null;
    }
    if (leavingNotebookId) {
      purgeWriteQueueForNotebook(leavingNotebookId);
    }

    set({
      sessionEpoch: get().sessionEpoch + 1,
      contentReadyEpoch: -1,
      routeVisit: -1,
      hydrated: false,
      notebookId: null,
      notebook: createEmptyNotebook(""),
      activeTabId: "",
      title: "",
      locked: false,
      lifecycleView: 'active',
      isDeleted: false,
      restoreRequiresFolder: false,
      parentFolderId: null,
      parentFolderName: null,
      workspaceId: null,
      restoreBusy: false,
      isPinned: false,
      isArchived: false,
      isDirty: false,
      isSaving: false,
      lastSaved: null,
      lastSavedAt: null,
      saveStatus: "idle",
      currentPage: 0,
      launcherOpen: false,
      launcherTarget: { kind: "notebook" },
      lastSpawnSectionId: null,
      richTextInsertSectionId: null,
      mediaAppendSectionId: null,
      referenceAppendSectionId: null,
      freemiumPrompt: null,
      planAccessPrompt: null,
      planReadOnly: false,
    });
    nbLog("store", "endNotebookSession DONE", { sessionEpoch: get().sessionEpoch });
  },

  /**
   * Synchronous Zustand-only session reset (safe during render).
   * DOM / Tiptap teardown runs in NotebookLayout useEffect when Shell is unmounted.
   */
  beginNotebookSession: (notebookId, routeVisit, options = {}) => {
    const { force = false } = options
    if (!notebookId || typeof notebookId !== 'string') {
      nbWarn('beginNotebookSession skipped — invalid notebookId', { notebookId, routeVisit })
      return
    }

    const current = get()
    const sessionReady =
      current.hydrated &&
      current.contentReadyEpoch === current.sessionEpoch
    if (
      !force &&
      current.notebookId === notebookId &&
      current.routeVisit === routeVisit &&
      current.routeVisit !== -1 &&
      sessionReady
    ) {
      nbLog('store', 'beginNotebookSession SKIP (ready)', { notebookId, routeVisit })
      return
    }

    const inflightKey = `${notebookId}:${routeVisit}:${current.sessionEpoch}`
    if (
      current.notebookId === notebookId &&
      current.routeVisit === routeVisit &&
      current.routeVisit !== -1 &&
      !sessionReady &&
      hydrateInflight.has(inflightKey)
    ) {
      nbLog('store', 'beginNotebookSession SKIP (hydrate in flight)', {
        notebookId,
        routeVisit,
        inflightKey,
      })
      return
    }

    const prev = {
      sessionEpoch: get().sessionEpoch,
      hydrated: get().hydrated,
      notebookId: get().notebookId,
      routeVisit: get().routeVisit,
      contentReadyEpoch: get().contentReadyEpoch,
    };
    nbLog('store', 'beginNotebookSession START', {
      notebookId,
      routeVisit,
      prev,
    });

    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active instanceof HTMLElement) active.blur();
    }

    for (const timer of sectionTimers.values()) {
      clearTimeout(timer);
    }
    sectionTimers.clear();
    if (metaTimer) {
      clearTimeout(metaTimer);
      metaTimer = null;
    }
    purgeWriteQueueForNotebook(notebookId);
    clearNotebookLocalCache(notebookId);

    const empty = createEmptyNotebook(notebookId);
    const nextEpoch = get().sessionEpoch + 1;
    set({
      sessionEpoch: nextEpoch,
      contentReadyEpoch: -1,
      routeVisit,
      hydrated: false,
      notebookId,
      notebook: empty,
      activeTabId: empty.tabs[0]?.id ?? "",
      title: "",
      locked: false,
      lifecycleView: 'active',
      isDeleted: false,
      restoreRequiresFolder: false,
      parentFolderId: null,
      parentFolderName: null,
      workspaceId: null,
      restoreBusy: false,
      isPinned: false,
      isArchived: false,
      isDirty: false,
      isSaving: false,
      lastSaved: null,
      lastSavedAt: null,
      saveStatus: "idle",
      currentPage: 0,
      launcherOpen: false,
      launcherTarget: { kind: "notebook" },
      lastSpawnSectionId: null,
      richTextInsertSectionId: null,
      mediaAppendSectionId: null,
      referenceAppendSectionId: null,
      freemiumPrompt: null,
      planAccessPrompt: null,
      planReadOnly: false,
    });
    nbLog('store', 'beginNotebookSession DONE', {
      notebookId,
      sessionEpoch: get().sessionEpoch,
      hydrated: get().hydrated,
    });
    nbSnapshot('after-beginNotebookSession', { notebookId });
  },

  hydrate: async (notebookId) => {
    const visitAtStart = get().routeVisit
    const epochAtStart = get().sessionEpoch
    const inflightKey = `${notebookId}:${visitAtStart}:${epochAtStart}`

    const existing = hydrateInflight.get(inflightKey)
    if (existing) {
      nbLog('store', 'hydrate COALESCE', { notebookId, inflightKey })
      return existing
    }

    const task = (async () => {
      nbLog('store', 'hydrate START', { notebookId, visitAtStart, epochAtStart })
      set({ isSaving: true })

      const isHydrateLive = () =>
        get().notebookId === notebookId &&
        get().routeVisit === visitAtStart &&
        get().sessionEpoch === epochAtStart

      try {
        const result = await persistLoad(notebookId)
        if (!isHydrateLive()) {
          nbWarn('hydrate STALE (api result)', {
            notebookId,
            visitAtStart,
            epochAtStart,
            currentVisit: get().routeVisit,
            currentEpoch: get().sessionEpoch,
          })
          return
        }
        if (result?.notebook) {
          const { notebook, activeTabId } = notebookFromApi(
            notebookId,
            result.notebook,
            result.sections ?? [],
            result.blocks ?? [],
          )
          const savedAt = result.notebook.updated_at
            ? new Date(result.notebook.updated_at)
            : new Date()
          const notebookClean = dedupeNotebookSections(notebook)
          const readyEpoch = get().sessionEpoch
          const lifecycle = lifecycleFieldsFromApi(result.notebook)
          set({
            hydrated: true,
            contentReadyEpoch: readyEpoch,
            notebookId,
            notebook: notebookClean,
            activeTabId,
            title: notebook.title ?? 'Untitled',
            ...lifecycle,
            isPinned: result.notebook.is_pinned ?? false,
            isDirty: false,
            isSaving: false,
            lastSaved: savedAt,
            lastSavedAt: savedAt.toISOString(),
            saveStatus: 'saved',
            currentPage: 0,
            launcherOpen: false,
          })
          if (lifecycle.locked) deactivateActiveEditor()
          logContentReadyEpoch('hydrate-api', notebookId, readyEpoch)
          await get().ensureStructuralSections()
          if (!isHydrateLive()) {
            nbWarn('hydrate STALE (after ensure api)', { notebookId, epochAtStart })
            return
          }
          if (get().sessionEpoch !== readyEpoch) {
            nbWarn('hydrate DONE but session moved on (api)', {
              notebookId,
              readyEpoch,
              sessionEpoch: get().sessionEpoch,
            })
            return
          }
          writeNotebookLocalCache(
            notebookId,
            {
              notebook: notebookClean,
              activeTabId,
              locked: lifecycle.locked,
            },
            { serverUpdatedAt: result.notebook.updated_at ?? null, source: 'server' },
          )
          nbLog('store', 'hydrate DONE (api)', {
            notebookId,
            sectionCount: notebookClean.tabs?.reduce((n, t) => n + t.sections.length, 0),
          })
          nbSnapshot('after-hydrate-api', { notebookId, epochAtStart })
          return
        }
      } catch (err) {
        if (!isHydrateLive()) {
          nbWarn('hydrate STALE (catch)', { notebookId, epochAtStart })
          return
        }
        if (
          err instanceof NotebookApiError &&
          err.code === 'FREE_NOTEBOOK_LIMIT'
        ) {
          set({
            freemiumPrompt: 'notebook',
            hydrated: true,
            contentReadyEpoch: get().sessionEpoch,
            notebookId,
            notebook: createEmptyNotebook(notebookId),
            activeTabId: createEmptyNotebook(notebookId).tabs[0].id,
            isDirty: false,
            isSaving: false,
          })
          return
        }
        if (err instanceof NotebookApiError) {
          nbWarn('hydrate FAILED (api) — not using localStorage fallback', {
            notebookId,
            status: err.status,
            code: err.code,
          })
          if (isHydrateLive()) {
            const readyEpoch = get().sessionEpoch
            set({
              hydrated: true,
              contentReadyEpoch: readyEpoch,
              isSaving: false,
              saveStatus: 'error',
            })
          }
          return
        }
      }

      if (!isHydrateLive()) return

      const local = readNotebookLocalCache(notebookId)
      if (local?.notebook) {
        const notebook = dedupeNotebookSections(
          expandBlankNotebookTabs(local.notebook),
        )
        const readyEpoch = get().sessionEpoch
        set({
          hydrated: true,
          contentReadyEpoch: readyEpoch,
          notebookId,
          notebook,
          activeTabId: local.activeTabId ?? notebook.tabs[0]?.id,
          isDirty: false,
          isSaving: false,
          lastSaved: local.savedAt ? new Date(local.savedAt) : null,
        })
        logContentReadyEpoch('hydrate-local', notebookId, readyEpoch)
        await get().ensureStructuralSections()
        if (!isHydrateLive()) {
          nbWarn('hydrate STALE (local)', { notebookId, epochAtStart })
          return
        }
        if (get().sessionEpoch !== readyEpoch) return
        nbLog('store', 'hydrate DONE (localStorage)', { notebookId, epochAtStart })
        nbSnapshot('after-hydrate-local', { notebookId, epochAtStart })
        return
      }

      if (!isHydrateLive()) return

      const empty = createEmptyNotebook(notebookId)
      const readyEpoch = get().sessionEpoch
      set({
        hydrated: true,
        contentReadyEpoch: readyEpoch,
        notebookId,
        notebook: empty,
        activeTabId: empty.tabs[0].id,
        isDirty: false,
        isSaving: false,
      })
      logContentReadyEpoch('hydrate-empty', notebookId, readyEpoch)
      await get().ensureStructuralSections()
      if (!isHydrateLive()) {
        nbWarn('hydrate STALE (empty)', { notebookId, epochAtStart })
        return
      }
      if (get().sessionEpoch !== readyEpoch) return
      nbLog('store', 'hydrate DONE (empty)', { notebookId, epochAtStart })
      nbSnapshot('after-hydrate-empty', { notebookId, epochAtStart })
    })()

    hydrateInflight.set(inflightKey, task)
    try {
      await task
    } finally {
      hydrateInflight.delete(inflightKey)
    }
  },

  /**
   * Hydrate from server-provided public payload (no API / no localStorage).
   * Used by /p/[slug] readonly publication pages only.
   */
  hydrateFromPublic: (payload) => {
    const notebookId = payload?.notebook?.id
    if (!notebookId) return

    const { notebook, activeTabId } = notebookFromApi(
      notebookId,
      {
        ...payload.notebook,
        is_published: true,
        is_locked: true,
        read_only: true,
      },
      payload.sections ?? [],
      payload.blocks ?? [],
    )

    const notebookClean = dedupeNotebookSections(notebook)
    const stamp = payload?.notebook?.updated_at
    const epoch =
      typeof stamp === 'string' && stamp.length > 0
        ? Math.max(1, Math.abs(hashString(stamp)) % 2147483646)
        : 1

    set({
      publicMode: true,
      routeVisit: 0,
      sessionEpoch: epoch,
      contentReadyEpoch: epoch,
      hydrated: true,
      notebookId,
      notebook: notebookClean,
      activeTabId,
      title: notebookClean.title ?? 'Untitled',
      locked: true,
      isPinned: false,
      isArchived: false,
      isDirty: false,
      isSaving: false,
      launcherOpen: false,
      saveStatus: 'saved',
      currentPage: 0,
    })

    nbLog('store', 'hydrateFromPublic DONE', {
      notebookId,
      slug: payload.notebook.public_slug,
    })
  },

  syncLifecycle: async () => {
    const { notebookId, publicMode } = get();
    if (!notebookId || publicMode) return;

    const existing = lifecycleSyncInflight.get(notebookId);
    if (existing) return existing;

    const task = (async () => {
      try {
        const res = await fetch(`/api/notebook/${notebookId}/status`, {
          credentials: 'include',
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false) return;
        const lifecycle = json.data?.lifecycle;
        if (!lifecycle) return;

        const prevLocked = get().locked;
        const fields = lifecycleFieldsFromApi({
          read_only: lifecycle.read_only,
          plan_read_only: lifecycle.plan_read_only,
          is_deleted: lifecycle.is_deleted,
          lifecycle_view: lifecycle.lifecycle_view,
          restore_requires_folder: lifecycle.restore_requires_folder,
          parent_folder_id: lifecycle.parent_folder_id,
          parent_folder_name: lifecycle.parent_folder_name,
          workspace_id: lifecycle.workspace_id,
          is_archived: lifecycle.lifecycle_view === 'archived',
        });

        const s = get();
        const unchanged =
          s.locked === fields.locked &&
          s.lifecycleView === fields.lifecycleView &&
          s.isDeleted === fields.isDeleted &&
          s.restoreRequiresFolder === fields.restoreRequiresFolder &&
          s.parentFolderId === fields.parentFolderId &&
          s.parentFolderName === fields.parentFolderName &&
          s.workspaceId === fields.workspaceId &&
          s.isArchived === fields.isArchived &&
          s.planReadOnly === fields.planReadOnly;
        if (unchanged) return;

        if (!prevLocked && fields.locked) {
          cancelAllSectionSaveTimers();
          purgeWriteQueueForNotebook(notebookId);
          if (metaTimer) {
            clearTimeout(metaTimer);
            metaTimer = null;
          }
          set({
            launcherOpen: false,
            richTextInsertSectionId: null,
            mediaAppendSectionId: null,
            referenceAppendSectionId: null,
          });
          deactivateActiveEditor();
        }

        if (fields.locked) {
          deactivateActiveEditor();
        }

        set({
          ...fields,
          saveStatus: fields.locked ? 'saved' : get().saveStatus,
        });
      } catch {
        /* best-effort sync */
      } finally {
        lifecycleSyncInflight.delete(notebookId);
      }
    })();

    lifecycleSyncInflight.set(notebookId, task);
    return task;
  },

  restoreFromTrash: async () => {
    const {
      notebookId,
      restoreRequiresFolder,
      parentFolderId,
      workspaceId,
      restoreBusy,
      locked,
    } = get();
    if (!notebookId || restoreBusy) return;
    set({ restoreBusy: true });
    try {
      if (restoreRequiresFolder && parentFolderId && workspaceId) {
        const res = await fetch('/api/folders', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            id: parentFolderId,
            workspace_id: workspaceId,
            is_deleted: false,
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(
            typeof body?.error === 'string'
              ? body.error
              : 'Could not restore folder',
          );
          return;
        }
        toast.success('Folder restored');
      } else {
        const res = await fetch('/api/scrolls/restore', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id: notebookId }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast.error(
            typeof body?.error === 'string'
              ? body.error
              : 'Could not restore note',
          );
          return;
        }
        toast.success('Note restored');
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('inora:trash-changed'));
      }
      await get().hydrate(notebookId);
    } finally {
      set({ restoreBusy: false });
    }
  },

  persistSnapshot: async () => {
    if (get().publicMode || get().locked) return;
    const { notebookId, notebook, activeTabId, locked } = get();
    if (!notebookId) return;
    set({ isSaving: true });
    try {
      await persistSave(notebookId, { notebook, activeTabId, locked });
      set({ isDirty: false, isSaving: false, lastSaved: new Date() });
    } catch {
      set({ isSaving: false });
    }
  },

  setActiveTab: (tabId) => {
    if (!get().notebook.tabs.some((t) => t.id === tabId)) return;
    void get().flushPendingSaves();
    set({ activeTabId: tabId, currentPage: 0, isDirty: true });
    const { notebookId, notebook } = get();
    scheduleMetaSave(
      notebookId,
      { id: notebookId, theme: buildTheme(notebook, tabId) },
      {
        onSaving: () => set({ isSaving: true }),
        onSaved: () => get().markSaved(),
        onError: (err) => {
          if (handlePlanReadOnlySaveError(set, err)) return;
          set({ isSaving: false, isDirty: true });
        },
      },
      get,
    );
  },

  addTab: () => {
    if (isNotebookInteractionLocked(get)) return;
    const { notebook } = get();
    if (notebook.tabs.length >= MAX_TABS) {
      toast.message("Maximum 8 tabs reached.");
      return;
    }
    const tab = createTab(`Tab ${notebook.tabs.length + 1}`);
    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: [...nb.tabs, tab],
    }));
    set({ activeTabId: tab.id });
    get()._saveMeta();
  },

  renameTab: (tabId, label) => {
    if (isNotebookInteractionLocked(get)) return;
    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tabId ? { ...t, label: clampTabTitle(label) } : t,
      ),
    }));
    get()._saveMeta();
  },

  deleteTab: async (tabId) => {
    const { notebook, activeTabId, notebookId } = get();
    if (notebook.tabs.length <= 1) return;
    const doomed = notebook.tabs.find((t) => t.id === tabId);
    const nextTabs = notebook.tabs.filter((t) => t.id !== tabId);
    const nextActive = activeTabId === tabId ? nextTabs[0].id : activeTabId;
    patchNotebook(set, get, (nb) => ({ ...nb, tabs: nextTabs }));
    set({ activeTabId: nextActive, isDirty: true });
    if (notebookId && doomed?.sections?.length) {
      for (const sec of doomed.sections) {
        try {
          await apiDeleteSection(notebookId, sec.id);
        } catch {
          /* best-effort */
        }
      }
    }
    get()._saveMeta();
  },

  setNotebookMeta: (title, subtitle, emoji) => {
    patchNotebook(set, get, (nb) => ({
      ...nb,
      title: title ?? nb.title,
      subtitle: subtitle ?? nb.subtitle,
      emoji: emoji ?? nb.emoji,
    }));
    get()._saveMeta();
  },

  setPublishState: (is_published, public_slug = null, public_short_id = null) => {
    patchNotebook(set, get, (nb) => ({
      ...nb,
      is_published,
      public_slug: is_published
        ? (public_slug ?? nb.public_slug)
        : nb.public_slug,
      public_short_id: is_published
        ? (public_short_id ?? nb.public_short_id)
        : nb.public_short_id,
    }));
  },

  setTitle: (title) => {
    if (isNotebookInteractionLocked(get)) return;
    const safe = title.trim() || "Untitled";
    patchNotebook(set, get, (nb) => ({ ...nb, title: safe }));
    set({ title: safe });
    get()._saveMeta({ title: safe });
  },

  setLocked: (locked) => {
    set({ locked });
    get()._saveMeta({ is_locked: locked });
  },

  setPinned: async (pinned) => {
    const { notebookId } = get();
    if (!notebookId) throw new Error("No notebook");
    set({ isPinned: pinned, isSaving: true });
    await updateNotebookMeta(notebookId, { id: notebookId, is_pinned: pinned });
    get().markSaved();
  },

  setArchived: async (archived) => {
    const { notebookId } = get();
    if (!notebookId) throw new Error("No notebook");
    const wasLocked = get().notebook?.is_locked ?? false;
    set({
      isArchived: archived,
      locked: archived ? true : wasLocked,
      isSaving: true,
    });
    await updateNotebookMeta(notebookId, {
      id: notebookId,
      is_archived: archived,
    });
    get().markSaved();
  },

  deleteNotebook: async () => {
    const { notebookId } = get();
    if (!notebookId) throw new Error("No notebook");
    await apiDeleteNotebook(notebookId);
  },

  _saveMeta: (extra = {}) => {
    if (get().publicMode || get().locked) return;
    const { notebookId, notebook, activeTabId } = get();
    if (!notebookId) return;
    const metaTitle = (extra.title ?? notebook.title)?.trim() || "Untitled";
    syncScrollFieldsToQueryCache(notebookId, { title: metaTitle });
    scheduleMetaSave(
      notebookId,
      {
        id: notebookId,
        title: notebook.title || "Untitled",
        icon: notebook.emoji,
        description: notebook.subtitle,
        theme: buildTheme(notebook, activeTabId),
        ...extra,
      },
      {
        onSaving: () => set({ isSaving: true }),
        onSaved: () => get().markSaved(),
        onError: (err) => {
          if (handlePlanReadOnlySaveError(set, err)) return;
          set({ isSaving: false, isDirty: true });
        },
      },
      get,
    );
    saveToLocalStorage(notebookId, {
      notebook,
      activeTabId,
      locked: get().locked,
    });
  },

  updateTabsSection: (sectionId, runtimeData) => {
    if (isNotebookInteractionLocked(get)) return;
    const normalized = normalizeTabsBlockData(runtimeData);
    patchTabsSection(set, get, sectionId, normalized);
    set({ isDirty: true, saveStatus: "pending" });
    const { notebookId } = get();
    scheduleSectionSave(
      notebookId,
      sectionId,
      normalized,
      createSectionSaveHooks(set, get),
      get,
    );
  },

  updateTabBlock: (sectionId, tabId, blockId, content) => {
    if (isNotebookInteractionLocked(get)) return;
    const { notebook, notebookId } = get();
    let section = null;
    for (const t of notebook.tabs) {
      section = t.sections.find((s) => s.id === sectionId);
      if (section) break;
    }
    if (!section) return;

    const tabsData = normalizeTabsBlockData(section.data);
    const nextData = normalizeTabsBlockData({
      ...tabsData,
      tabs: tabsData.tabs.map((t) =>
        t.id === tabId
          ? {
              ...t,
              blocks: t.blocks.map((b) =>
                b.id === blockId ? { ...b, data: content } : b,
              ),
            }
          : t,
      ),
    });
    patchTabsSection(set, get, sectionId, nextData);
    set({ isDirty: true, saveStatus: "pending" });
    scheduleBlockSave(notebookId, blockId, content, {
      onSaving: () => set({ isSaving: true, saveStatus: "saving" }),
      onSaved: () => get().markSaved(),
      onError: () =>
        set({ isDirty: true, isSaving: false, saveStatus: "error" }),
    });
  },

  removeTabBlock: async (sectionId, tabId, blockId) => {
    const { notebook, notebookId } = get();
    let section = null;
    for (const t of notebook.tabs) {
      section = t.sections.find((s) => s.id === sectionId);
      if (section) break;
    }
    if (!section) return;

    const tabsData = normalizeTabsBlockData(section.data);
    const tab = tabsData.tabs.find((t) => t.id === tabId);
    const removed = tab?.blocks.find((b) => b.id === blockId);
    const nextData = normalizeTabsBlockData({
      ...tabsData,
      tabs: tabsData.tabs.map((t) =>
        t.id === tabId
          ? { ...t, blocks: t.blocks.filter((b) => b.id !== blockId) }
          : t,
      ),
    });
    patchTabsSection(set, get, sectionId, nextData);
    set({ isDirty: true, saveStatus: "pending" });

    if (notebookId) {
      scheduleSectionSave(
        notebookId,
        sectionId,
        nextData,
        createSectionSaveHooks(set, get),
        get,
      );
      if (removed) {
        try {
          await deleteNotebookBlock(notebookId, blockId);
        } catch {
          /* section JSON is source of truth */
        }
      }
    }
  },

  addBlockInsideTabs: async (tabsSectionId, tabId, rawType) => {
    const type = resolveBlockType(rawType);
    if (type === "block_tabs") {
      toast.error("Tabs cannot be nested inside tabs.");
      return null;
    }
    if (!isImplementedType(type)) return null;

    const { notebook, notebookId } = get();
    let section = null;
    for (const t of notebook.tabs) {
      section = t.sections.find((s) => s.id === tabsSectionId);
      if (section) break;
    }
    if (!section || resolveBlockType(section.type) !== "block_tabs")
      return null;

    const tabsData = normalizeTabsBlockData(section.data);
    const tab = tabsData.tabs.find((t) => t.id === tabId);
    if (!tab) return null;

    const prevKey = tab.blocks[tab.blocks.length - 1]?.sort_key ?? null;
    const sortKey = safeGenerateKeyBetween(prevKey, null);
    const blockId = crypto.randomUUID();
    const newBlock = {
      id: blockId,
      type,
      data: getDefaultData(type),
      sort_key: sortKey,
    };
    const nextData = normalizeTabsBlockData({
      ...tabsData,
      tabs: tabsData.tabs.map((t) =>
        t.id === tabId ? { ...t, blocks: [...t.blocks, newBlock] } : t,
      ),
    });

    patchTabsSection(set, get, tabsSectionId, nextData);
    set({ launcherOpen: false, launcherTarget: { kind: "notebook" } });

    if (!notebookId) return blockId;

    scheduleSectionSave(
      notebookId,
      tabsSectionId,
      nextData,
      createSectionSaveHooks(set, get),
      get,
    );

    try {
      await addNotebookBlock(notebookId, {
        id: blockId,
        section_id: tabsSectionId,
        tab_id: tabId,
        type,
        content: newBlock.data,
        sort_key: sortKey,
        position: tab.blocks.length,
      });
    } catch {
      /* notebook_blocks optional — section JSON already saved above */
    }
    return blockId;
  },

  ensureTabsSection: async () => {
    if (get().hasTabsBlock()) return null;

    const { notebook, notebookId, activeTabId } = get();
    const tab =
      notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0];
    if (!tab) return null;

    const hero = tab.sections.find(
      (s) => resolveBlockType(s.type) === "block_hero",
    );
    const afterHero = tab.sections.find(
      (s) => s.sort_key && hero?.sort_key && s.sort_key > hero.sort_key,
    );
    const sortKey = safeGenerateKeyBetween(
      hero?.sort_key ?? null,
      afterHero?.sort_key ?? null,
    );
    const tempId = crypto.randomUUID();
    const type = "block_tabs";
    const data = getDefaultData(type);
    const optimistic = { id: tempId, type, data, sort_key: sortKey };

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) => {
        if (t.id !== tab.id) return t;
        const heroIdx = t.sections.findIndex(
          (s) => resolveBlockType(s.type) === "block_hero",
        );
        const insertAt = heroIdx >= 0 ? heroIdx + 1 : 0;
        const sections = [...t.sections];
        sections.splice(insertAt, 0, optimistic);
        return { ...t, sections };
      }),
    }));
    set({ isDirty: true });

    if (!notebookId) return tempId;

    try {
      const created = await apiAddSection(notebookId, {
        type,
        data,
        sort_key: sortKey,
        position: tab.sections.length,
        page_id: tab.id,
      });
      if (created?.id) {
        patchNotebook(set, get, (nb) => ({
          ...nb,
          tabs: nb.tabs.map((t) =>
            t.id === tab.id
              ? {
                  ...t,
                  sections: t.sections.map((s) =>
                    s.id === tempId ? { ...s, id: created.id } : s,
                  ),
                }
              : t,
          ),
        }));
        remapSectionSaveTimer(
          tempId,
          created.id,
          notebookId,
          get,
          createSectionSaveHooks(set, get),
        );
      }
      get().markSaved();
      return created?.id ?? tempId;
    } catch {
      cancelSectionSaveTimer(tempId);
      patchNotebook(set, get, (nb) => ({
        ...nb,
        tabs: nb.tabs.map((t) =>
          t.id === tab.id
            ? { ...t, sections: t.sections.filter((s) => s.id !== tempId) }
            : t,
        ),
      }));
      return null;
    }
  },

  ensureStructuralSections: async () => {
    const { notebook } = get();
    const hasHero = notebook.tabs.some((t) =>
      t.sections.some((s) => resolveBlockType(s.type) === "block_hero"),
    );
    const needsTabs = !get().hasTabsBlock();
    const tasks = [];
    if (!hasHero) {
      tasks.push(get().ensureHeroSection({ ...HERO_BLOCK_DEFAULTS }));
    }
    if (needsTabs) {
      tasks.push(get().ensureTabsSection());
    }
    if (tasks.length) await Promise.all(tasks);
  },

  ensureHeroSection: async (data = HERO_BLOCK_DEFAULTS) => {
    const { notebook, notebookId, activeTabId } = get();
    const hasHero = notebook.tabs.some((t) =>
      t.sections.some((s) => resolveBlockType(s.type) === "block_hero"),
    );
    if (hasHero) return null;

    const tab =
      notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0];
    if (!tab) return null;

    const sortKey = safeGenerateKeyBetween(null, tab.sections[0]?.sort_key ?? null);
    const tempId = crypto.randomUUID();
    const type = "block_hero";
    const optimistic = { id: tempId, type, data, sort_key: sortKey };

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tab.id ? { ...t, sections: [optimistic, ...t.sections] } : t,
      ),
    }));

    if (isNotebookInteractionLocked(get)) {
      set({ isDirty: false, saveStatus: "saved" });
      return tempId;
    }

    set({ isDirty: true });

    if (!notebookId) return tempId;

    try {
      const created = await apiAddSection(notebookId, {
        type,
        data,
        sort_key: sortKey,
        position: 0,
        page_id: tab.id,
      });
      if (created?.id) {
        patchNotebook(set, get, (nb) => ({
          ...nb,
          tabs: nb.tabs.map((t) =>
            t.id === tab.id
              ? {
                  ...t,
                  sections: t.sections.map((s) =>
                    s.id === tempId ? { ...s, id: created.id } : s,
                  ),
                }
              : t,
          ),
        }));
        remapSectionSaveTimer(
          tempId,
          created.id,
          notebookId,
          get,
          createSectionSaveHooks(set, get),
        );
      }
      get().markSaved();
      return created?.id ?? tempId;
    } catch (err) {
      if (err instanceof NotebookApiError && err.code === "PLAN_READ_ONLY") {
        set({ isDirty: false, saveStatus: "saved" });
        return tempId;
      }
      cancelSectionSaveTimer(tempId);
      patchNotebook(set, get, (nb) => ({
        ...nb,
        tabs: nb.tabs.map((t) =>
          t.id === tab.id
            ? { ...t, sections: t.sections.filter((s) => s.id !== tempId) }
            : t,
        ),
      }));
      return null;
    }
  },

  /**
   * Edge + on list-style blocks: append a row/point inside the same section.
   * Returns true when handled; false → caller may insert a new section after.
   */
  appendSectionInline: (sectionId) => {
    const { notebook, activeTabId } = get();
    const tab =
      notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0];
    if (!tab) return false;
    const section = tab.sections.find((s) => s.id === sectionId);
    if (!section) return false;

    const type = resolveBlockType(section.type);
    const base =
      section.data && typeof section.data === "object"
        ? { .../** @type {Record<string, unknown>} */ (section.data) }
        : {};

    switch (type) {
      case "block_rule":
      case "block_card":
      case "block_supporting_notes": {
        const rules = ensureRuleRowsWithIds(
          Array.isArray(base.rules) ? base.rules : [],
        );
        get().updateSection(sectionId, {
          ...base,
          rules: [...rules, createRuleRow("")],
        });
        return true;
      }
      case "block_steps": {
        const steps = ensureStepRowsWithIds(
          Array.isArray(base.steps) ? base.steps : [],
        );
        get().updateSection(sectionId, {
          ...base,
          steps: [...steps, createStepRow("New step")],
        });
        return true;
      }
      case "block_step_row": {
        const steps = Array.isArray(base.steps) ? [...base.steps] : [];
        const n = steps.length + 1;
        get().updateSection(sectionId, {
          ...base,
          steps: [
            ...steps,
            {
              num: String(n),
              title: `Step ${n}`,
              detail: "Describe what happens in this step.",
            },
          ],
        });
        return true;
      }
      case "block_timeline": {
        const normalized = normalizeJourneyTimelineData(base);
        if (normalized.status !== "draft") return false;
        const milestones = [...normalized.milestones];
        if (milestones.length >= MAX_DAYS) return false;
        const next = [
          ...milestones,
          createMilestone(`Day ${milestones.length + 1}`, ""),
        ];
        get().updateSection(sectionId, {
          ...normalized,
          dayCount: next.length,
          milestones: next,
        });
        return true;
      }
      case "block_zone": {
        const zones = Array.isArray(base.zones) ? [...base.zones] : [];
        if (!zones.length) return false;
        const zone = { ...(zones[0] ?? {}) };
        const items = Array.isArray(zone.items) ? [...zone.items] : [];
        items.push("");
        zones[0] = { ...zone, items };
        get().updateSection(sectionId, { ...base, zones });
        return true;
      }
      default:
        return false;
    }
  },

  /** Insert a block directly after a section (inline add — no launchpad). */
  insertSectionAfter: async (afterSectionId, rawType = "block_richtext") => {
    const type = resolveBlockType(rawType);
    if (isStructuralSectionType(type)) return null;
    if (!isImplementedType(type)) return null;

    const { notebook, notebookId, activeTabId } = get();
    if (isTabBlockLimitReached(notebook, activeTabId)) {
      notifyTabBlockAddBlocked();
      return null;
    }
    const tab =
      notebook.tabs.find((t) => t.id === activeTabId) ?? notebook.tabs[0];
    if (!tab) return null;

    const sections = tab.sections;
    const idx = sections.findIndex((s) => s.id === afterSectionId);
    if (idx < 0) return null;

    const beforeKey = sections[idx]?.sort_key ?? null;
    const afterKey = sections[idx + 1]?.sort_key ?? null;
    const sortKey = safeGenerateKeyBetween(beforeKey, afterKey);
    const tempId = crypto.randomUUID();
    const data = getDefaultData(type);
    const optimistic = { id: tempId, type, data, sort_key: sortKey };

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) => {
        if (t.id !== tab.id) return t;
        const next = [...t.sections];
        next.splice(idx + 1, 0, optimistic);
        return { ...t, sections: next };
      }),
    }));
    set({ isDirty: true });

    if (!notebookId) return tempId;

    try {
      const created = await apiAddSection(notebookId, {
        type,
        data,
        sort_key: sortKey,
        position: idx + 1,
        page_id: tab.id,
      });
      if (created?.id) {
        patchNotebook(set, get, (nb) => ({
          ...nb,
          tabs: nb.tabs.map((t) =>
            t.id === tab.id
              ? {
                  ...t,
                  sections: t.sections.map((s) =>
                    s.id === tempId ? { ...s, ...created } : s,
                  ),
                }
              : t,
          ),
        }));
        remapSectionSaveTimer(
          tempId,
          created.id,
          notebookId,
          get,
          createSectionSaveHooks(set, get),
        );
      }
      get()._saveMeta();
      get().markSaved();
      return created.id;
    } catch (err) {
      cancelSectionSaveTimer(tempId);
      patchNotebook(set, get, (nb) => ({
        ...nb,
        tabs: nb.tabs.map((t) =>
          t.id === tab.id
            ? { ...t, sections: t.sections.filter((s) => s.id !== tempId) }
            : t,
        ),
      }));
      if (err instanceof NotebookApiError) {
        if (err.code === "PLAN_READ_ONLY") {
          set({ planAccessPrompt: "note", planReadOnly: true, locked: true });
        } else if (err.code === "FREE_NOTE_LIMIT") set({ freemiumPrompt: "note" });
        else if (err.code === "FREE_NOTEBOOK_LIMIT")
          set({ freemiumPrompt: "notebook" });
        else if (!err.code?.startsWith("FREE_")) {
          toast.error(err.message || "Could not add block");
        }
      } else {
        toast.error("Could not add block");
      }
      return null;
    }
  },

  addSection: async (tabIdOrType, maybeType) => {
    const ctx = get().launcherTarget;
    const rawType = maybeType ?? tabIdOrType;
    const type = resolveBlockType(rawType);

    if (ctx?.kind === "tab") {
      return get().addBlockInsideTabs(ctx.tabsSectionId, ctx.tabId, type);
    }

    if (isStructuralSectionType(type)) {
      return null;
    }

    const tabId = get().activeTabId;
    if (!isImplementedType(type)) return null;
    const { notebook, notebookId } = get();
    if (isTabBlockLimitReached(notebook, tabId)) {
      notifyTabBlockAddBlocked();
      return null;
    }
    const tab = notebook.tabs.find((t) => t.id === tabId);
    if (!tab) return null;

    const sections = tab.sections;
    const prevKey = sections[sections.length - 1]?.sort_key ?? null;
    const sortKey = safeGenerateKeyBetween(prevKey, null);
    const tempId = crypto.randomUUID();
    const data = getDefaultData(type);

    const optimistic = {
      id: tempId,
      type,
      data,
      sort_key: sortKey,
    };

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tab.id ? { ...t, sections: [...t.sections, optimistic] } : t,
      ),
    }));
    set({
      launcherOpen: false,
      isDirty: true,
      lastSpawnSectionId: tempId,
    });

    if (!notebookId) return tempId;

    try {
      const created = await apiAddSection(notebookId, {
        type,
        data,
        sort_key: sortKey,
        position: sections.length,
        page_id: tab.id,
      });
      if (created?.id) {
        patchNotebook(set, get, (nb) => ({
          ...nb,
          tabs: nb.tabs.map((t) =>
            t.id === tab.id
              ? {
                  ...t,
                  sections: t.sections.map((s) =>
                    s.id === tempId ? { ...s, ...created } : s,
                  ),
                }
              : t,
          ),
        }));
        remapSectionSaveTimer(
          tempId,
          created.id,
          notebookId,
          get,
          createSectionSaveHooks(set, get),
        );
      }
      get()._saveMeta();
      get().markSaved();
      return created.id;
    } catch (err) {
      cancelSectionSaveTimer(tempId);
      patchNotebook(set, get, (nb) => ({
        ...nb,
        tabs: nb.tabs.map((t) =>
          t.id === tab.id
            ? { ...t, sections: t.sections.filter((s) => s.id !== tempId) }
            : t,
        ),
      }));
      if (err instanceof NotebookApiError) {
        if (err.code === "PLAN_READ_ONLY") {
          set({ planAccessPrompt: "note", planReadOnly: true, locked: true });
        } else if (err.code === "FREE_NOTE_LIMIT") set({ freemiumPrompt: "note" });
        else if (err.code === "FREE_NOTEBOOK_LIMIT")
          set({ freemiumPrompt: "notebook" });
        else if (!err.code?.startsWith("FREE_")) {
          toast.error(err.message || "Could not add block");
        }
      } else {
        toast.error("Could not add block");
      }
      return null;
    }
  },

  updateSection: (tabIdOrSectionId, sectionIdOrData, maybeData) => {
    if (isNotebookInteractionLocked(get)) return;
    let tabId;
    let sectionId;
    let data;
    if (maybeData !== undefined) {
      tabId = tabIdOrSectionId;
      sectionId = sectionIdOrData;
      data = maybeData;
    } else {
      sectionId = tabIdOrSectionId;
      data = sectionIdOrData;
      tabId =
        findTabIdForSection(get().notebook, sectionId) ?? get().activeTabId;
    }
    const tid = tabId || get().activeTabId;
    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tid
          ? {
              ...t,
              sections: t.sections.map((s) =>
                s.id === sectionId ? { ...s, data } : s,
              ),
            }
          : t,
      ),
    }));
    set({ isDirty: true, saveStatus: "pending" });

    const { notebookId } = get();
    scheduleSectionSave(
      notebookId,
      sectionId,
      data,
      createSectionSaveHooks(set, get),
      get,
    );
  },

  deleteSection: async (tabId, sectionId) => {
    const tid =
      tabId ||
      findTabIdForSection(get().notebook, sectionId) ||
      get().activeTabId;
    const { notebook, notebookId } = get();
    const tab = notebook.tabs.find((t) => t.id === tid);
    const removed = tab?.sections.find((s) => s.id === sectionId);

    cancelSectionSaveTimer(sectionId);

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tid
          ? { ...t, sections: t.sections.filter((s) => s.id !== sectionId) }
          : t,
      ),
    }));
    set({ isDirty: true });

    if (notebookId && removed) {
      try {
        await apiDeleteSection(notebookId, sectionId);
        get().markSaved();
      } catch (err) {
        if (err instanceof NotebookApiError && err.status === 404) {
          get().markSaved();
          return;
        }
        patchNotebook(set, get, (nb) => ({
          ...nb,
          tabs: nb.tabs.map((t) =>
            t.id === tid
              ? {
                  ...t,
                  sections: [...t.sections, removed].sort((a, b) =>
                    a.sort_key < b.sort_key ? -1 : 1,
                  ),
                }
              : t,
          ),
        }));
      }
    }
  },

  moveSection: (tabId, sectionId, direction) => {
    const tid = tabId || get().activeTabId;
    const { notebook } = get();
    const tab = notebook.tabs.find((t) => t.id === tid);
    if (!tab) return;

    const idx = tab.sections.findIndex((s) => s.id === sectionId);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= tab.sections.length) return;

    const next = [...tab.sections];
    const beforeKey =
      direction === "up"
        ? (next[target - 1]?.sort_key ?? null)
        : next[target].sort_key;
    const afterKey =
      direction === "up"
        ? next[target].sort_key
        : (next[target + 1]?.sort_key ?? null);

    next[idx] = {
      ...next[idx],
      sort_key: safeGenerateKeyBetween(beforeKey, afterKey),
    };
    [next[idx], next[target]] = [next[target], next[idx]];

    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) => (t.id === tid ? { ...t, sections: next } : t)),
    }));
    set({ isDirty: true });

    const { notebookId } = get();
    if (notebookId) {
      const items = next.map(({ id, sort_key }) => ({
        id,
        sort_key,
      }));
      apiReorderSections(notebookId, items).then(
        () => get().markSaved(),
        () => set({ isDirty: true }),
      );
    }
  },

  removeSection: (sectionId) => {
    const { notebook, activeTabId } = get();
    const legacy = findLegacyInnerBlockLocation(
      notebook,
      activeTabId,
      sectionId,
    );
    if (legacy) {
      return get().removeTabBlock(
        legacy.tabsSectionId,
        legacy.innerTabId,
        legacy.blockId,
      );
    }
    const ownerTabId = findTabIdForSection(notebook, sectionId);
    return get().deleteSection(ownerTabId, sectionId);
  },

  getPages: () => {
    const sections = getActiveTabContentSections(
      get().notebook,
      get().activeTabId,
    );
    return sections.length ? [sections] : [[]];
  },

  reorderSections: async (newOrder) => {
    const tid = get().activeTabId;
    patchNotebook(set, get, (nb) => ({
      ...nb,
      tabs: nb.tabs.map((t) =>
        t.id === tid ? { ...t, sections: newOrder } : t,
      ),
    }));
    const { notebookId } = get();
    if (notebookId) {
      const items = newOrder.map(({ id, sort_key }) => ({
        id,
        sort_key,
      }));
      try {
        await apiReorderSections(notebookId, items);
        get().markSaved();
      } catch {
        set({ isDirty: true });
      }
    }
  },
});

export function createNotebookStore() {
  return create(notebookStateCreator);
}

export { useNotebook, useNotebook as useNotebookStore } from './use-notebook';
